import React from 'react';
import { motion } from 'framer-motion';
import { getCheckoutHref, trackCheckoutClick } from '../utils/checkout';
type Tone = 'cream' | 'white' | 'mint' | 'violet';
const toneClasses: Record<Tone, string> = {
  cream: 'bg-brand-cream text-brand-ink',
  white: 'bg-white text-brand-ink',
  mint: 'bg-brand-mint text-brand-ink',
  violet: 'bg-brand-violet text-brand-cream'
};
type SectionProps = {
  children: React.ReactNode;
  id?: string;
  tone?: Tone;
  className?: string;
  innerClassName?: string;
  decor?: React.ReactNode;
};
export function Section({
  children,
  id,
  tone = 'cream',
  className = '',
  innerClassName = '',
  decor
}: SectionProps) {
  return <section id={id} className={`relative scroll-mt-16 overflow-hidden ${toneClasses[tone]} ${className}`} data-id="element-206" data-mp="peo2vleiff2p">
      {decor && <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-207" data-mp="1vv9jmp01s1ty">
          {decor}
        </div>}
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className={`relative mx-auto w-full max-w-page px-5 py-16 md:px-8 md:py-24 ${innerClassName}`} data-id="element-208" data-mp="20t1laaqhv2bc">
        {children}
      </motion.div>
    </section>;
}
type TagVariant = 'mint' | 'orange' | 'violet' | 'white';
const tagClasses: Record<TagVariant, string> = {
  mint: 'bg-brand-mint text-brand-violet',
  orange: 'bg-brand-orange text-brand-violet',
  violet: 'bg-brand-violet text-brand-cream',
  white: 'bg-white text-brand-violet'
};
export function Tag({
  children,
  variant = 'mint',
  className = ''
}: {
  children: React.ReactNode;
  variant?: TagVariant;
  className?: string;
}) {
  return <span className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs font-bold tracking-wide sm:text-sm ${tagClasses[variant]} ${className}`} data-id="element-209" data-mp="2cvl5a1x6qsrg">
      {children}
    </span>;
}
type SectionHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  tag?: React.ReactNode;
  tagVariant?: TagVariant;
  className?: string;
};
export function SectionHeader({
  title,
  subtitle,
  tag,
  tagVariant = 'mint',
  className = ''
}: SectionHeaderProps) {
  return <div className={`mx-auto max-w-3xl text-center ${className}`} data-id="element-210" data-mp="1jb2h1nbl59hn">
      {tag && <Tag variant={tagVariant} className="mb-4" data-id="element-211" data-mp="1o82ej9udd3x5">
          {tag}
        </Tag>}
      <h2 className="text-balance text-[32px] font-extrabold leading-[1.1] text-brand-violet md:text-5xl" data-id="element-212" data-mp="2byq0v0517w38">{title}</h2>
      {subtitle && <p className="mt-4 text-balance text-base leading-relaxed text-brand-ink/80 md:text-xl" data-id="element-213" data-mp="2gwhqtom4984s">{subtitle}</p>}
    </div>;
}
type CTAWidth = 'responsive' | 'full' | 'auto';
const widthClasses: Record<CTAWidth, string> = {
  responsive: 'w-full sm:w-auto',
  full: 'w-full',
  auto: 'w-auto'
};
type CTAButtonProps = {
  children: React.ReactNode;
  className?: string;
  width?: CTAWidth;
};

/** Botón de compra principal. Usa siempre la misma URL de checkout (o #oferta si no está configurada). */
export function CTAButton({
  children,
  className = '',
  width = 'responsive'
}: CTAButtonProps) {
  return <a href={getCheckoutHref()} onClick={trackCheckoutClick} className={`inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl border-b-4 border-brand-orangeDark bg-brand-orange px-6 py-3 text-center text-[15px] font-bold leading-tight text-brand-violet shadow-cta transition-[transform,background-color,box-shadow] duration-150 ease-out hover:-translate-y-0.5 hover:bg-brand-orangeLight hover:shadow-cta-hover active:translate-y-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-violet focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:px-8 sm:text-base md:text-lg ${widthClasses[width]} ${className}`} data-id="element-214" data-mp="j95ssweoerzy">
      {children}
    </a>;
}